<section class="alert-response-page">

    <div class="alert-response-shell status-card" *ngIf="pageState === 'loading'">
        <app-progress-spinner></app-progress-spinner>
        <p>{{ 'Loading...' | translate }}</p>
    </div>

    <div class="alert-response-shell status-card" *ngIf="pageState === 'error'">
        <h2>{{ loadErrorTitle }}</h2>
        <p>{{ loadErrorMessage }}</p>
        <button type="button" class="ack-confirm" *ngIf="canRetryLoad" (click)="retryLoad()">
            {{ 'Try again' | translate }}
        </button>
    </div>

    <div class="alert-response-shell status-card" *ngIf="pageState === 'revoked'">
        <h2>{{ 'This link has been revoked.' | translate }}</h2>
        <p>{{ 'Nothing was recorded. Tell your supervisor the alert reached the wrong handset.' | translate }}</p>
    </div>

    <div class="alert-response-shell" *ngIf="pageState === 'ready' && data as page">

        <div class="ack-identity">
            <div class="k">{{ 'You are acknowledging as' | translate }}</div>
            <div class="who">{{ page.recipientName || ('This link recipient' | translate) }}</div>
            <div class="meta">
                <span *ngIf="page.recipientMaskedPhone" class="link-sent">
                    {{ 'Link sent by SMS to' | translate }} {{ page.recipientMaskedPhone }}
                </span>
                <span *ngIf="page.tokenExpiresAt" class="link-sent">
                    &middot; {{ 'expires' | translate }} {{ page.tokenExpiresAt | dateFormat: timeFormat }}
                </span>
                <ng-container *ngIf="!isConfirmingRevoke">
                    &middot;
                    <button type="button" class="not-you" (click)="startRevoke()">
                        {{ 'Not you?' | translate }}
                    </button>
                </ng-container>
            </div>

            <div class="revoke-confirm" *ngIf="isConfirmingRevoke">
                <p>{{ 'This revokes the link so nobody can use it. Nothing is recorded.' | translate }}</p>
                <div class="revoke-actions">
                    <button type="button" class="revoke-yes" [disabled]="revokePending" (click)="confirmRevoke()">
                        {{ 'Revoke this link' | translate }}
                    </button>
                    <button type="button" class="revoke-no" [disabled]="revokePending" (click)="cancelRevoke()">
                        {{ 'Cancel' | translate }}
                    </button>
                </div>
            </div>
        </div>

        <header class="ack-head" [ngClass]="headerSeverityClass">
            <span class="severity-chip compact" [ngClass]="headerSeverityClass">{{ headerSeverityLabel | translate }}</span>
            <div class="rule">{{ headerTitle }}</div>
            <div class="meta">
                <span *ngIf="page.siteName" class="meta">{{ page.siteName }}</span>
                <span *ngIf="page.detectedAt" class="meta">
                    &middot; {{ 'detected' | translate }} <b>{{ page.detectedAt | dateFormat: timeFormat }}</b>
                </span>
                <br />
                {{ breachSummaryLabel }}
            </div>
        </header>

        <div class="acked-banner" *ngIf="isAcknowledged">
            <mat-icon>check</mat-icon>
            <div>
                <b>{{ acknowledgementLabel }}</b>
            </div>
        </div>

        <div class="transition-list" *ngIf="transitionRows.length">
            <div class="transitioned" *ngFor="let transition of transitionRows; trackBy: trackById">
                <mat-icon>check</mat-icon>
                <b>{{ transition.nodeName }}</b> &middot; {{ transition.fromLabel }} &rarr; {{ transition.toLabel }}
            </div>
        </div>
        <section class="ack-sec" *ngIf="!isAcknowledged">
            <div class="ack-sec-title">{{ 'Where this is' | translate }}</div>

            <div class="ack-map-bar" *ngIf="isMapUsable">
                <span class="who">{{ page.siteName || ('Alerting nodes' | translate) }}</span>
                <span class="n">&middot; {{ locateSummaryLabel }}</span>
                <button type="button" class="ack-map-recentre" (click)="recentreMap()">
                    <mat-icon>my_location</mat-icon>
                    {{ 'Recentre' | translate }}
                </button>
            </div>

            <div class="map-anchor" #locateAnchor></div>

            <div class="ack-map-down" *ngIf="mapState === 'failed'">
                <mat-icon>warning</mat-icon>
                <div>
                    <b>{{ "The map didn't load." | translate }}</b>
                    {{ 'No connection to the basemap.' | translate }}
                    {{ 'The alert, the readings and the acknowledgement below are all here and all work - they came with the page.' | translate }}
                    <button type="button" class="retry" (click)="retryMap()">
                        <mat-icon>refresh</mat-icon>
                        {{ 'Try the map again' | translate }}
                    </button>
                </div>
            </div>

            <div class="ack-map-down" *ngIf="!hasMappableNodes">
                <mat-icon>warning</mat-icon>
                <div>
                    <b>{{ 'No recorded locations for these nodes.' | translate }}</b>
                    {{ 'Coverage is selected from the node list.' | translate }}
                </div>
            </div>

            <div class="ack-map-foot" *ngIf="isMapUsable">
                <span class="key"><i></i> {{ 'alerting' | translate }}</span>
                <span class="key"><i class="plain"></i> {{ 'nearby' | translate }}</span>
                <br />
                {{ 'Nothing here is tappable yet. Coverage is recorded after you confirm, on the same map.' | translate }}
            </div>
        </section>

        <section class="ack-sec" *ngIf="readingRows.length">
            <div class="ack-sec-title">
                {{ 'What happened' | translate }} <span class="n">{{ readingRows.length }}</span>
                {{ 'nodes' | translate }}
            </div>

            <article
                class="reading-row"
                [class.nodata]="!reading.isReporting"
                *ngFor="let reading of readingRows; trackBy: trackById">
                <span class="rn">
                    <span class="severity-chip compact" [ngClass]="reading.severityClass">
                        {{ reading.severityLabel | translate }}
                    </span>
                    {{ reading.nodeName }}
                </span>
                <span class="rv" [ngClass]="reading.bandTextClass">{{ reading.readingValue | nullDisplay }}</span>
                <span class="ru">{{ reading.unitLabel }}</span>
                <span class="rt" *ngIf="reading.startedAt">{{ reading.startedAt | dateFormat: timeFormat }}</span>
                <span class="rlim" *ngIf="reading.thresholdValue != null">
                    {{ 'Rule threshold' | translate }} <b>{{ reading.thresholdValue }}</b> {{ reading.unitLabel }}
                    <ng-container *ngIf="reading.detailLabel">&middot; {{ reading.detailLabel }}</ng-container>
                </span>
                <span class="rnd" *ngIf="!reading.isReporting">
                    <mat-icon>signal_cellular_off</mat-icon>
                    <span>
                        {{ 'No data since' | translate }}
                        <b>{{ reading.lastReportedAt | dateFormat: timeFormat }}</b>.
                        {{ 'The alert is still open - a node that stopped reporting has not come back under the threshold.' | translate }}
                    </span>
                </span>
            </article>
        </section>

        <section class="ack-sec" *ngIf="!isAcknowledged && page.canAcknowledge">
            <div class="ack-scope-note ack-escalated-note" *ngIf="wasEscalated">
                <mat-icon>info</mat-icon>
                <span>{{ 'This alert was escalated before being acknowledged. You can still acknowledge it below.' | translate }}</span>
            </div>
            <button type="button" class="ack-confirm" [disabled]="acknowledgePending" (click)="acknowledge()">
                <mat-icon>check</mat-icon>
                {{ acknowledgePending ? ('Recording...' | translate) : ('I have seen this' | translate) }}
            </button>
            <div class="ack-note">
                {{ 'Covers all' | translate }} <strong>{{ readingRows.length }}</strong>
                {{ 'nodes listed above.' | translate }}
                <br />
                {{ 'Records you, and the time. Nothing else on this page is required.' | translate }}
            </div>
        </section>

        <ng-container *ngIf="isAcknowledged && page.canCaptureResponse">
            <section class="ack-sec">
                <div class="ack-sec-title">{{ 'What did you do?' | translate }}</div>

                <div class="ack-scope-note">
                    <mat-icon>info</mat-icon>
                    <span>
                        {{ 'The suggestion is at the top because the rule named it.' | translate }}
                        <strong>{{ 'You are not limited to it' | translate }}</strong>
                        {{ '- pick whatever you actually did.' | translate }}
                    </span>
                </div>

                <div class="ctrl-search">
                    <mat-icon>search</mat-icon>
                    <input
                        type="text"
                        [placeholder]="'Search controls...' | translate"
                        [ngModel]="controlSearchText"
                        (ngModelChange)="onControlSearchChanged($event)" />
                </div>

                <ng-container *ngFor="let group of controlGroups; trackBy: trackById">
                    <div class="ctrl-group">
                        {{ group.title }}
                        <span class="why" *ngIf="group.hint">{{ group.hint }}</span>
                    </div>
                    <button
                        type="button"
                        class="ctrl-opt"
                        [class.suggested]="control.isSuggested"
                        [class.on]="selectedControlIds[control.id]"
                        [disabled]="responseSubmitted"
                        *ngFor="let control of group.options; trackBy: trackById"
                        (click)="toggleControl(control.id)">
                        <span class="box"></span>
                        <span class="lbl">
                            {{ control.name }}
                            <span class="sub" *ngIf="control.categoryName || control.lastUsedAt">
                                <ng-container *ngIf="control.categoryName">{{ control.categoryName }}</ng-container>
                                <ng-container *ngIf="control.lastUsedAt">
                                    &middot; {{ 'last used' | translate }}
                                    {{ control.lastUsedAt | dateFormat: dateTimeFormat }}
                                    <ng-container *ngIf="control.lastUsedByName">
                                        {{ 'by' | translate }} {{ control.lastUsedByName }}
                                    </ng-container>
                                </ng-container>
                            </span>
                        </span>
                        <span class="rank-tag" *ngIf="control.isSuggested">{{ 'From rule' | translate }}</span>
                    </button>
                </ng-container>

                <button
                    type="button"
                    class="ctrl-opt ctrl-more"
                    *ngIf="hiddenControlCount > 0 || showAllControls"
                    (click)="toggleShowAllControls()">
                    <mat-icon>{{ showAllControls ? 'expand_less' : 'expand_more' }}</mat-icon>
                    <span class="lbl" *ngIf="!showAllControls">
                        {{ 'Show all' | translate }} {{ controlCount }}
                    </span>
                    <span class="lbl" *ngIf="showAllControls">{{ 'Show fewer' | translate }}</span>
                </button>

                <div class="ack-map-down" *ngIf="!hasControlMatches">
                    <mat-icon>warning</mat-icon>
                    <div>{{ 'No controls match that search.' | translate }}</div>
                </div>
            </section>

            <section class="ack-sec">
                <div class="ack-sec-title">{{ 'When did it start?' | translate }}</div>
                <div class="started-row">
                    <span class="node-chip locked">{{ startedAt | dateFormat: timeFormat }}</span>
                    <button type="button" class="node-chip" *ngIf="!isEditingStartedAt" (click)="startEditingStartedAt()">
                        <mat-icon>schedule</mat-icon>
                        {{ 'Change' | translate }}
                    </button>
                    <ng-container *ngIf="isEditingStartedAt">
                        <input
                            class="form-input"
                            [owlDateTimeTrigger]="startedAtPicker"
                            [owlDateTime]="startedAtPicker"
                            [ngModel]="startedAt"
                            (ngModelChange)="onStartedAtChanged($event)"
                            [max]="maxStartedAt"
                            readonly
                            [placeholder]="'When did it start?' | translate" />
                        <owl-date-time #startedAtPicker (afterPickerClosed)="confirmStartedAt()"></owl-date-time>
                    </ng-container>
                </div>
                <div class="form-help">
                    {{ 'The time you say it started, not the time you typed it.' | translate }}
                </div>
            </section>

            <section class="ack-sec">
                <div class="ack-sec-title">{{ 'Which nodes does this cover?' | translate }}</div>

                <div class="seg">
                    <button
                        type="button"
                        [class.on]="coverageSurface === 'map'"
                        [class.dead]="!isMapUsable"
                        (click)="selectCoverageSurface('map')">
                        <mat-icon>map</mat-icon>
                        {{ 'Map' | translate }}
                        <span class="why" *ngIf="!isMapUsable">{{ 'off' | translate }}</span>
                    </button>
                    <button type="button" [class.on]="coverageSurface === 'list'" (click)="selectCoverageSurface('list')">
                        <mat-icon>format_list_bulleted</mat-icon>
                        {{ 'List' | translate }}
                    </button>
                </div>

                <ng-container *ngIf="coverageSurface === 'map'">
                    <div class="ack-map-bar">
                        <span class="who">{{ 'Tap a node to add or remove it' | translate }}</span>
                        <button type="button" class="ack-map-recentre" (click)="recentreMap()">
                            <mat-icon>my_location</mat-icon>
                            {{ 'Recentre' | translate }}
                        </button>
                    </div>
                    <div class="map-anchor" #coverageAnchor></div>
                </ng-container>

                <div class="ack-map-down" *ngIf="!isMapUsable">
                    <mat-icon>warning</mat-icon>
                    <div>
                        <b>{{ 'No map, so you are picking from the list.' | translate }}</b>
                        {{ 'Same nodes, same selection, nearest first.' | translate }}
                        <button type="button" class="retry" *ngIf="hasMappableNodes" (click)="retryMap()">
                            <mat-icon>refresh</mat-icon>
                            {{ 'Try the map again' | translate }}
                        </button>
                    </div>
                </div>

                <ng-container *ngIf="showCoverageList">
                    <div class="node-group" *ngIf="lockedNodeRows.length">
                        <div class="node-group-title">{{ 'The nodes that alerted' | translate }}</div>
                        <span class="node-chip locked" *ngFor="let node of lockedNodeRows; trackBy: trackById">
                            <mat-icon>check</mat-icon> {{ node.name }}
                        </span>
                    </div>

                    <div class="node-group" *ngIf="nearbyNodeRows.length">
                        <div class="node-group-title">{{ 'Nearby - tap to add' | translate }}</div>
                        <button
                            type="button"
                            class="node-chip"
                            [class.on]="node.isCovered"
                            [disabled]="responseSubmitted"
                            *ngFor="let node of nearbyNodeRows; trackBy: trackById"
                            (click)="toggleNodeCoverage(node.id)">
                            <mat-icon *ngIf="node.isCovered">check</mat-icon>
                            <mat-icon *ngIf="!node.isCovered">add</mat-icon>
                            {{ node.name }}
                            <span class="dist">{{ node.distanceLabel }}</span>
                        </button>
                    </div>

                    <div class="node-group" *ngIf="unlocatedNodeRows.length">
                        <div class="node-group-title">{{ 'No recorded location' | translate }}</div>
                        <button
                            type="button"
                            class="node-chip"
                            [class.on]="node.isCovered"
                            [disabled]="responseSubmitted"
                            *ngFor="let node of unlocatedNodeRows; trackBy: trackById"
                            (click)="toggleNodeCoverage(node.id)">
                            <mat-icon *ngIf="node.isCovered">check</mat-icon>
                            <mat-icon *ngIf="!node.isCovered">add</mat-icon>
                            {{ node.name }}
                            <span class="dist">{{ node.distanceLabel }}</span>
                        </button>
                    </div>
                </ng-container>

                <div class="cover-out">
                    <div class="k">
                        {{ 'Covering' | translate }} <b>{{ coveredNodeRows.length }}</b> {{ 'nodes' | translate }}
                    </div>
                    <ng-container *ngFor="let node of coveredNodeRows; trackBy: trackById">
                        <span class="node-chip locked" *ngIf="node.isLocked">
                            <mat-icon>check</mat-icon> {{ node.name }}
                        </span>
                        <button
                            type="button"
                            class="node-chip on"
                            *ngIf="!node.isLocked"
                            [disabled]="responseSubmitted"
                            (click)="toggleNodeCoverage(node.id)">
                            <mat-icon>close</mat-icon> {{ node.name }}
                        </button>
                    </ng-container>
                </div>

                <div class="ack-map-foot">
                    <span class="key"><i class="sel"></i> {{ 'covered' | translate }}</span>
                    <span class="key"><i class="plain"></i> {{ 'not covered' | translate }}</span>
                    <br />
                    {{ 'The alerting nodes are covered by default and cannot be removed here. There is no radius field and no area to draw.' | translate }}
                </div>
            </section>

            <section class="ack-sec">
                <div class="ack-sec-title">
                    {{ 'Anything to add?' | translate }}
                    <span class="soft">&mdash; {{ 'optional' | translate }}</span>
                </div>
                <textarea
                    rows="3"
                    class="form-textarea"
                    [placeholder]="'Free text, if the controls above do not say it' | translate"
                    [disabled]="responseSubmitted"
                    [ngModel]="responseNote"
                    (ngModelChange)="onResponseNoteChanged($event)"></textarea>
            </section>

            <section class="ack-sec">
                <button
                    type="button"
                    class="ack-confirm"
                    [class.done]="responseSubmitted"
                    [disabled]="responsePending || disableSubmitResponse"
                    (click)="submitResponse()">
                    <mat-icon>check</mat-icon>
                    {{ responseSubmitted ? ('Response recorded' | translate) : ('Save what I did' | translate) }}
                </button>
                <div class="ack-note">
                    {{ selectedControlCount }} {{ 'controls' | translate }} &middot;
                    {{ coveredNodeRows.length }} {{ 'nodes' | translate }} &middot;
                    {{ 'from' | translate }} {{ startedAt | dateFormat: timeFormat }}
                    <br />
                    {{ 'You can add more later from the same link.' | translate }}
                </div>
                <button type="button" class="ack-back" (click)="goToPreviousStep()">
                    <mat-icon>arrow_back</mat-icon>
                    {{ 'Back' | translate }}
                </button>
            </section>
        </ng-container>

        <div class="map-parking">
            <div
                class="ack-map-frame"
                #mapFrame
                [class.readonly]="!isAcknowledged"
                [class.loading]="mapState === 'loading'"
                [class.failed]="mapState === 'failed'">
                <div class="map-canvas"></div>
            </div>
        </div>
    </div>
</section>
